// ═══════════════════════════════════════════════════════════════════
// fieldCodex.js — SHIP 9 · THE FIELD CODEX AND THE LEGAL PLAYS
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "Add a way to not only see all Primeras and Role
// definitions etc (information you're expected to memorize) while on
// the Shatterfield. Take it a step further, have a way to see every
// available effect currently on the board and what they belong to —
// just effects that can currently legally be played by the player."
//
// TWO PURE FUNCTIONS, NO REACT, NO FIREBASE:
//   · codexEntries()   — the reference. The rift Arts, the Mind's
//     Silence, Exiliation, the Ritual, the Primera law, every stamp
//     word's effect, the two Warden options, the four Exiliation
//     options. Rule text comes from RULEZ_V2's own glossary lines
//     (rulezV2.js is the rulebook as a string) so the pill can never
//     say something the book does not — if a line is wrong, fix
//     RULEZ_V2.md. The stamp effects come from rules.PRIMERAS, which is
//     the table the seals themselves are built from.
//   · legalPlaysFor()  — the live question. Every effect on MY side of
//     the board that I could activate right now, each with the SAME
//     verdict the radial wedge would show: the rule gates (ruleGate),
//     the fare law (R107 — one pool, no mixing), the cadence tags
//     (R122, soulUsed / lastUsedTurn), R35's wait on a set Tome, the
//     Primera seals' own ready/spent/locked, artsRailFor for the Arts.
//     Nothing face-down of the adversary's, nothing in a hand, nothing
//     in a Necropolis. A play the gate refuses is listed DIM with the
//     gate's reason, because "why can't I" is half of the question.
//
// ⚠ THIS FILE IS A VIEW, NEVER A DOOR. Nothing here mutates anything.
// caP'n, 2026-09-16, second ask: *"make the options playable from the
// pill … very 4xS"* — so each legal item now carries a `play`
// descriptor ({kind, slotIdx, axis, cardId, optionId}), and Shatterfield's
// `handlePillPlay` walks it through THE SAME DOOR the board uses (the
// rift's Art opener, the seal's onPress, the Tome wedge's own onSelect,
// the Soul modal's activate, the Exiliation option pick). No second
// gate, no second resolver: if this list and a wedge ever disagree,
// the wedge is the evidence and this file is what is wrong.
// ═══════════════════════════════════════════════════════════════════
import {
  ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, CURIOS, ESCLINGS, PROPHECIES,
} from "./cards";
import {
  PRIMERAS, PRIMERA_COST, RIFTS, ROLES, roleFare, roleCooldown, ROLE_CADENCE,
  WARDEN_OPTIONS, EXILIATION_OPTIONS, exileFare, exiliationCooldown,
  printedFare, canPayPrintedFare, farePoolsFor, cooldownHalfTurns,
  tomeWaitHalfTurns, soulCardOffCooldown, ESCLING_SLOTS, TOME_SLOTS, CURIO_SLOTS,
  STARTING_ANIMA,
} from "./rules";
import { artsRailFor, EXILIATION_KEY } from "./roleArts";
import { gateTomeActivation, gateSoulActivation, gateExiliation } from "./ruleGate";
import { needsWielder } from "./tomeResolver";
import { tomeIsStanding } from "./npcTomeBook";
import { baseId } from "./necroSeal";
import { RULEZ_V2_MD } from "./rulezV2";

const ALL = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...CURIOS, ...ESCLINGS, ...PROPHECIES];
const cardOf = (id) => (id ? ALL.find((c) => c.id === baseId(id)) || null : null);
const lvlOf = (card) => (card?.level === "xxx" ? 3 : Number(card?.level) || 1);
// RTDB drops null entries and hands arrays back as sparse integer-keyed
// objects ({0:a, 3:b}); Object.values would slide rift 3 into rift 1.
// Same shape as Shatterfield's riftArray: fixed length, holes kept.
const dense = (v, len) => {
  const out = new Array(len).fill(null);
  if (Array.isArray(v)) { for (let i = 0; i < len; i++) out[i] = v[i] ?? null; }
  else if (v && typeof v === "object") { Object.keys(v).forEach((k) => { const n = parseInt(k, 10); if (n >= 0 && n < len) out[n] = v[k]; }); }
  return out;
};

// ── THE BOOK'S OWN WORDS ──────────────────────────────────────────
// A glossary line in RULEZ_V2 is `**Term** — text`. Read once, cached.
let _gloss = null;
export function glossary() {
  if (_gloss) return _gloss;
  const out = {};
  // `**Term** — text`, bare or bulleted (`- **The Ritual** — …`).
  const re = /^(?:[-*]\s+)?\*\*([^*]+)\*\*\s+—\s+(.+)$/gm;
  let m;
  while ((m = re.exec(RULEZ_V2_MD)) !== null) {
    const term = m[1].trim();
    // The book's **emphasis** is markdown; the pill is plain text.
    if (!out[term]) out[term] = m[2].trim().replace(/\*\*/g, "");
  }
  _gloss = out;
  return out;
}
const say = (term) => glossary()[term] || null;

// ── THE CODEX ─────────────────────────────────────────────────────
// Sections in the order a player needs them at the table.
export function codexEntries(seatCount = 2) {
  const g = glossary();
  const fareText = (f) => (f ? `${f.khao} Khao or ${f.anima} Anima` : "free");
  const cadText = (role) => {
    const tag = ROLE_CADENCE[role];
    if (!tag) return "no cooldown";
    const ht = roleCooldown(role, seatCount);
    return `${tag} · once per ${Math.max(1, Math.round(ht / Math.max(1, seatCount)))} full Turn${ht > seatCount ? "s" : ""}`;
  };
  const fareByLevel = (role) => {
    const f = [1, 2, 3].map((l) => roleFare(role, l));
    const same = f.every((x) => (x?.khao ?? -1) === (f[0]?.khao ?? -1) && (x?.anima ?? -1) === (f[0]?.anima ?? -1));
    return same ? fareText(f[0]) : `${f.map((x) => x?.khao ?? 0).join(" / ")} Khao or Anima by the being's Level`;
  };
  const rifts = RIFTS.map((r) => ({
    title: `${r.name} — the ${r.role} (${r.element})`,
    text: g[r.name] || "",
    meta: `${fareByLevel(r.role)} · ${cadText(r.role)}`,
  }));
  const warden = WARDEN_OPTIONS.map((o) => ({ title: `Warden · ${o.nullifies === "effect" ? "nullify an effect" : "nullify a Lyt Attack"}`, text: o.text, meta: "OP2 · 5 Khao or 5 Anima · one Warden Art per Aenarchist per Turn" }));
  const stamps = [];
  ["ilk", "school"].forEach((axis) => {
    Object.entries(PRIMERAS[axis] || {}).forEach(([word, def]) => {
      stamps.push({ title: `${word} (${axis === "ilk" ? "Ilk" : "School"})`, text: def.effect, meta: "costs the being's Level — 1 / 2 / 3 — Khao or Anima, never split · own Turn only · one per being per Turn · one per word per Turn" });
    });
  });
  const exile = EXILIATION_OPTIONS.map((o) => ({ title: `Exiliation · ${o.id.replace(/_/g, " ")}`, text: o.text, meta: `fare = the exiled card's level (${exileFare(1)} / ${exileFare(2)} / ${exileFare(3)}) · once per 2 full Turns` }));
  return [
    { section: "The Four Rifts and their Arts", note: "Asleep for the first 3 full Turns. The rift owns the Art, not the being; Esclings wear none.", entries: rifts },
    { section: "The Mind's Silence", entries: [{ title: "The Mind's Silence", text: say("The Mind's Silence") || "", meta: "" }] },
    { section: "The Warden's two answers", entries: warden },
    { section: "Primeras — the stamps", note: say("Primera") || "", entries: stamps },
    { section: "Exile", note: say("Exiliation") || "", entries: [...exile, { title: "The Ritual", text: say("The Ritual") || "", meta: "" }] },
    { section: "States you will be asked about", entries: ["Fractured", "Fallen", "Deviant", "Natural", "Soul", "Ascend"].map((t) => ({ title: t, text: say(t) || "", meta: "" })).filter((e) => e.text) },
  ];
}

// ── THE LEGAL PLAYS ───────────────────────────────────────────────
// `stampsFor(slot, i)` is Shatterfield's own seal builder (ready / spent
// / locked, one legality snapshot for both boards); `gateCtx()` is its
// own gate context builder; `seatCount` the table size. The verdicts
// are theirs, not ours.
export function legalPlaysFor({ me, opponent, turn = 0, seatCount = 2, myTurn = false, stampsFor = null, gateCtx = null, responseOpen = false }) {
  const groups = [];
  if (!me) return groups;
  const ctx = typeof gateCtx === "function" ? gateCtx() : { me, opponent, turn, seatCount };
  const pools = { khao: me.khao || 0, anima: me.anima ?? STARTING_ANIMA };
  const rifts = dense(me.field?.esclings, ESCLING_SLOTS);
  const beingAt = (i) => cardOf(rifts[i]?.escendant);
  const gateReason = (v) => (v && !v.valid ? (v.reason || (v.violations || []).join(", ") || "the rules refuse it") : null);

  // 1 · THE ARTS — the rail the boards and the brain all read.
  try {
    const rail = artsRailFor(me, opponent, turn, seatCount);
    groups.push({
      title: "Role Arts",
      items: rail.map((r) => {
        const being = beingAt(r.rift);
        const f = r.fare ? `${r.fare.khao}K / ${r.fare.anima}A` : "free";
        return {
          label: `${r.name} · the ${r.role}`,
          owner: being ? being.name : "— empty rift —",
          cost: f,
          legal: !!r.legal && !!being,
          reason: !being ? "no being stands in this rift" : (r.reason || null),
          play: { kind: "art", slotIdx: r.rift },
        };
      }),
    });
  } catch (e) { groups.push({ title: "Role Arts", items: [], error: String(e?.message || e) }); }

  // 2 · THE STAMPS — the seals' own state.
  if (typeof stampsFor === "function") {
    const items = [];
    rifts.forEach((slot, i) => {
      const being = beingAt(i);
      if (!being) return;
      let st = null;
      try { st = stampsFor(slot, i); } catch (_) { st = null; }
      if (!st) return;
      const lvl = lvlOf(being);
      const cost = PRIMERA_COST[`L${lvl}`] || lvl;
      ["ilk", "school"].forEach((axis) => {
        const s = st[axis];
        if (!s || !s.word) return;
        const def = PRIMERAS?.[axis]?.[s.word];
        items.push({
          label: `${s.word} stamp (${axis === "ilk" ? "Ilk" : "School"})`,
          owner: `${being.name} · ${RIFTS[i]?.name || "rift " + i}`,
          cost: `${cost}K / ${cost}A`,
          legal: s.state === "ready",
          reason: s.state === "spent" ? "already pressed this Turn (one per being, one per word)"
            : s.state === "locked" ? (!myTurn ? "not your Turn" : responseOpen ? "a response window is open" : farePoolsFor(cost, pools).length ? "locked" : `cannot pay ${cost} from one pool`)
            : null,
          text: def?.effect || "",
          play: { kind: "stamp", slotIdx: i, axis },
        });
      });
    });
    groups.push({ title: "Primeras", items });
  }

  // 3 · THE TOMES — mine, on my rifts. Face-down = the flip; face-up = again.
  {
    const tomes = dense(me.field?.tomes, TOME_SLOTS);
    const items = [];
    tomes.forEach((t, i) => {
      const id = typeof t === "object" && t !== null ? t.id : t;
      const card = cardOf(id);
      if (!card) return;
      const faceDown = typeof t === "object" && t !== null ? !!t.faceDown : false;
      const placed = (typeof t === "object" && t !== null && t.placedTurn != null) ? t.placedTurn : turn;
      const waited = turn - placed;
      const fare = printedFare(card);
      const afford = canPayPrintedFare(card, pools);
      let v = null;
      try { v = gateTomeActivation(ctx, typeof t === "object" ? t : { id }, waited, faceDown); } catch (e) { v = { valid: false, reason: "gate threw" }; }
      let reason = gateReason(v);
      if (!reason && !myTurn) reason = "not your Turn";
      if (!reason && !afford) reason = fare.anima > 0 ? `cannot pay ${fare.khao} Khao and ${fare.anima} Anima` : `cannot pay ${fare.khao} Khao`;
      if (!reason && !faceDown) {
        const last = typeof t === "object" && t !== null ? t.lastUsedTurn : null;
        if (last != null && (turn - last) < cooldownHalfTurns(card.effectTag, seatCount)) reason = `on cooldown (${card.effectTag || "OPT"})`;
        else if (tomeIsStanding(id)) reason = "a standing Tome — its effect is already in force";
      }
      if (!reason && needsWielder(card) && !rifts.some((s) => s?.escendant)) reason = "needs a wielder — no being on your field";
      if (!reason && faceDown) {
        const need = tomeWaitHalfTurns(lvlOf(card), { faceDown: true, seatCount });
        if (waited < need) reason = `set this Turn — a Level ${lvlOf(card)} Tome waits ${Math.ceil(need / seatCount)} full Turn`;
      }
      items.push({
        label: `${faceDown ? "Flip & activate" : "Activate"} · ${card.name}`,
        owner: `Tome rift ${i + 1}${faceDown ? " (set, face-down)" : ""} · ${card.ilk}${card.effectTag ? " · " + card.effectTag : ""}`,
        cost: fare.anima > 0 ? `${fare.khao}K + ${fare.anima}A` : `${fare.khao}K`,
        legal: !reason,
        reason,
        text: card.effect || "",
        play: { kind: "tome", slotIdx: i, cardId: id, faceDown },
      });
    });
    groups.push({ title: "Tomes", items });
  }

  // 4 · THE SOUL — Impera, U'hlti, Affinity already attached to my beings.
  {
    const items = [];
    rifts.forEach((slot, i) => {
      const being = beingAt(i);
      if (!being) return;
      (Array.isArray(slot?.soul) ? slot.soul : Object.values(slot?.soul || {})).forEach((sid) => {
        const sc = cardOf(sid);
        if (!sc || (sc.type !== "Impera" && sc.type !== "U'hlti" && sc.type !== "Affinity")) return;
        const activatable = !!sc.effectTag;   // an untagged soul card is read, never pressed
        let v = null;
        try { v = gateSoulActivation(ctx, slot, sid); } catch (e) { v = { valid: false, reason: "gate threw" }; }
        let reason = gateReason(v);
        if (!reason && !myTurn && sc.type !== "U'hlti") reason = "not your Turn";
        if (!reason && !soulCardOffCooldown(slot, sid, sc.effectTag, turn, seatCount)) reason = `on cooldown (${sc.effectTag})`;
        const fare = printedFare(sc);
        if (!reason && !canPayPrintedFare(sc, pools)) reason = fare.anima > 0 ? `cannot pay ${fare.khao} Khao and ${fare.anima} Anima` : `cannot pay ${fare.khao} Khao`;
        items.push({
          label: `${sc.type} · ${sc.name}`,
          owner: `${being.name} · ${RIFTS[i]?.name || "rift " + i}${sc.effectTag ? " · " + sc.effectTag : ""}`,
          cost: fare.khao || fare.anima ? (fare.anima > 0 ? `${fare.khao}K + ${fare.anima}A` : `${fare.khao}K`) : "—",
          legal: activatable && !reason,
          passive: !activatable,   // read, never pressed — rendered as a gift, not a refusal
          reason: activatable ? reason : null,
          text: sc.effect || "",
          play: activatable ? { kind: "soul", slotIdx: i, cardId: sid } : null,
        });
      });
    });
    groups.push({ title: "On the Soul", items });
  }

  // 5 · THE CURIOS — what I am wearing. NOTHING ACTIVATES A CURIO
  //     (ruleGate GATE_REACH): a seat's Curios grant their gift once,
  //     automatically, at the opening of the duel. So they are listed as
  //     what they are — gifts already in force — never as a play.
  {
    const curios = dense(me.field?.curios, CURIO_SLOTS);
    const items = [];
    curios.forEach((c, i) => {
      const id = typeof c === "object" && c !== null ? c.id : c;
      const card = cardOf(id);
      if (!card) return;
      items.push({
        label: `Curio · ${card.name}`,
        owner: `Curio rift ${i + 1} · ${card.ilk || card.type}`,
        cost: "—",
        legal: false,
        passive: true,
        reason: null,
        text: `${card.effect || ""} (granted once, at the opening of the duel)`,
      });
    });
    groups.push({ title: "Curios — worn", items });
  }

  // 6 · EXILE — the four Exiliation options, each asked of the gate at the
  //     cheapest level, so "available at all" is honest and the option
  //     picked in the pit decides the real fare.
  {
    const last = me[EXILIATION_KEY] ?? null;
    const items = EXILIATION_OPTIONS.map((o) => {
      let v = null;
      // The gate takes the option ID (as roleArts.performExiliation does), never the object.
      try { v = gateExiliation(ctx, o.id, 1, { lastUsedTurn: last }); } catch (e) { v = { valid: false, reason: "gate threw" }; }
      let reason = gateReason(v);
      if (!reason && last != null && (turn - last) < exiliationCooldown(seatCount)) reason = "once per 2 full Turns — used";
      return {
        label: `Exiliation · ${o.id.replace(/_/g, " ")}`,
        owner: `from your ${o.exileFrom}`,
        cost: `${exileFare(1)}/${exileFare(2)}/${exileFare(3)}A by level`,
        legal: !reason,
        reason,
        text: o.text,
        play: { kind: "exile", optionId: o.id },
      };
    });
    groups.push({ title: "Exile", items });
  }

  return groups;
}

export const RIFT_COUNT = ESCLING_SLOTS;
export const ROLE_NAMES = ROLES;
